import React, { useEffect, useMemo, useState } from "react";
import { api, getToken, API } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import DepartmentChips from "@/components/DepartmentChips";
import DigestSettingsCard from "@/components/DigestSettingsCard";
import SenderEmailCard from "@/components/SenderEmailCard";
import EmailEventsCard from "@/components/EmailEventsCard";
import ReportsExtrasSection, { TurnoverCard } from "@/components/ReportsExtrasSection";
import SupplierSpendDetailDialog from "@/components/SupplierSpendDetailDialog";
import FixUnassignedDialog from "@/components/FixUnassignedDialog";
import AssetCategoryDrillDialog from "@/components/AssetCategoryDrillDialog";
import StockValueDetailDialog from "@/components/StockValueDetailDialog";
import TotalConsumedDetailDialog from "@/components/TotalConsumedDetailDialog";
import MachineSpendDrillDialog, { printReport } from "@/components/MachineSpendDrillDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useDepartments } from "@/contexts/DepartmentsContext";
import {
  DollarSign,
  Scissors,
  TrendingUp,
  BarChart3,
  PieChart as PieIcon,
  Activity,
  Download,
  Monitor,
  Cog,
  ShieldCheck,
  PackageX,
  Wrench,
  AlertCircle,
  Clock,
  Eye,
  Printer,
  Wand2,
  ChevronDown,
  TrendingDown,
  AlertTriangle,
  PhoneCall,
  X,
  ClipboardCheck,
} from "lucide-react";
import { Replace as ReplaceIcon } from "lucide-react";

const fmt = (n) =>
  (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Broadcast collapse-all / expand-all signals to every collapsible ChartCard
// (and the ad-hoc collapsible panels like Price changes). Sections subscribe
// via useEffect on the tick values — bumping the tick tells them what state
// to switch to, without owning individual booleans per section up here.
const CollapseAllContext = React.createContext({
  collapseTick: 0,
  expandTick: 0,
});

const FALLBACK_DEPT_COLOR = {
  Production: "#228EBF",
  Milling: "#7A4DBA",
  "3DP": "#1E8E8E",
  Reception: "#C2185B",
  Unassigned: "#9CA3AF",
};

const ChartCard = ({
  title,
  icon: Icon,
  children,
  testid,
  className = "",
  right = null,
  collapsible = true,
  defaultOpen = true,
  headerRight = null,
  collapsedPreview = null,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  // Subscribe to the parent Reports page's collapse-all / expand-all
  // signals. The tick counters bump when the user clicks either button,
  // so every collapsible card flips together without owning per-section
  // state at the top.
  const { collapseTick, expandTick } = React.useContext(CollapseAllContext);
  React.useEffect(() => {
    if (collapseTick > 0) setOpen(false);
  }, [collapseTick]);
  React.useEffect(() => {
    if (expandTick > 0) setOpen(true);
  }, [expandTick]);
  if (!collapsible) {
    return (
      <div className={`nb-card p-5 ${className}`} data-testid={testid}>
        <div className="flex items-center gap-2 mb-3">
          {Icon && <Icon size={20} strokeWidth={2.5} />}
          <h2 className="font-display text-xl flex-1">{title}</h2>
          {right}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className={`nb-card p-5 ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-2 text-left"
      >
        {Icon && <Icon size={20} strokeWidth={2.5} />}
        <h2 className="font-display text-xl flex-1 flex items-center gap-2">
          {title}
          {headerRight}
        </h2>
        {right}
        <ChevronDown
          size={16}
          strokeWidth={2.5}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className="mt-3">{open ? children : collapsedPreview}</div>
    </div>
  );
};

const Reports = () => {
  const { user, isManager } = useAuth();
  const { colorFor: deptColor } = useDepartments();
  const colorFor = (k) => deptColor(k) || FALLBACK_DEPT_COLOR[k] || "#001740";
  // Read initial state from URL search params so links like
  // `/reports?days=30&dept=3DP&tab=consumables` (from Dashboard chart taps) land
  // on the correct scope. One-shot read at mount — subsequent URL edits by the
  // user's back button won't retrigger state (kept simple on purpose).
  const initialParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const initialDays = (() => {
    const p = parseInt(initialParams.get("days") || "", 10);
    return [7, 30, 90, 365].includes(p) ? p : 30;
  })();
  const initialDept = initialParams.get("dept") || "all";
  const initialTab = ["consumables", "assets", "machinery"].includes(
    initialParams.get("tab"),
  )
    ? initialParams.get("tab")
    : "consumables";
  const [spend, setSpend] = useState(null);
  const [days, setDays] = useState(initialDays);
  // Optional explicit date range — when either is set, backend uses these
  // instead of `days`. `days` still drives the label on the presets.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Opening Balance was retired in v91 (the app now baselines via Stocktake
  // baseline mode instead). Any legacy opening-balance movements remain in
  // the DB but Reports permanently exclude them; there's no toggle any more.
  // Same idea for stocktake reconciliation movements — physical-count fixes
  // that would otherwise spike consumption or spend charts.
  const [includeStocktake, setIncludeStocktake] = useState(false);
  const useCustomRange = !!(dateFrom || dateTo);
  const addDateParams = (params) => {
    if (useCustomRange) {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else {
      params.days = days;
    }
    if (includeStocktake) params.include_stocktake = true;
    return params;
  };
  const [dept, setDept] = useState(initialDept);
  const [spendDetail, setSpendDetail] = useState(null); // {id, name} for drill-down
  const [costRows, setCostRows] = useState([]);
  const [stockValue, setStockValue] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [itemConsumption, setItemConsumption] = useState(null);
  const [priceChanges, setPriceChanges] = useState(null);
  const [shrinkage, setShrinkage] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [fixUnassignedOpen, setFixUnassignedOpen] = useState(false);
  const [assetCatDrill, setAssetCatDrill] = useState(null);
  const [stockValueDrillOpen, setStockValueDrillOpen] = useState(false);
  const [totalConsumedDrillOpen, setTotalConsumedDrillOpen] = useState(false);
  const [machineDrill, setMachineDrill] = useState(null); // selected machine row from Top spend

  // Assets + Machinery report state
  const [assetValue, setAssetValue] = useState(null);
  const [assetCats, setAssetCats] = useState(null);
  const [warranty, setWarranty] = useState(null);
  const [breakage, setBreakage] = useState(null);
  const [serviceSpend, setServiceSpend] = useState(null);
  const [siteServicesSummary, setSiteServicesSummary] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [downtime, setDowntime] = useState(null);
  const [calloutResponse, setCalloutResponse] = useState(null);
  // Combined Assets + Machinery value (one endpoint drives multiple widgets).
  const [holdings, setHoldings] = useState(null);
  const [holdingsExpanded, setHoldingsExpanded] = useState({}); // {dept: bool}
  // Price Changes panel starts collapsed — it can grow to hundreds of rows
  // during a stable price period and pushes the more actionable widgets
  // (Shrinkage, Supplier Spend) below the fold. User expands on demand.
  const [priceChangesExpanded, setPriceChangesExpanded] = useState(false);
  // Extra ad-hoc collapsible sections (not ChartCards) — Shrinkage &amp; Top
  // consumers. Reports owns the master collapse/expand ticks so these
  // subscribe via a plain useEffect right below, instead of the context
  // (which a component can't consume from itself).
  const [shrinkageExpanded, setShrinkageExpanded] = useState(true);
  const [topConsumersExpanded, setTopConsumersExpanded] = useState(true);
  const [collapseTick, setCollapseTick] = useState(0);
  const [expandTick, setExpandTick] = useState(0);
  useEffect(() => {
    if (collapseTick === 0) return;
    setPriceChangesExpanded(false);
    setShrinkageExpanded(false);
    setTopConsumersExpanded(false);
  }, [collapseTick]);
  useEffect(() => {
    if (expandTick === 0) return;
    setPriceChangesExpanded(true);
    setShrinkageExpanded(true);
    setTopConsumersExpanded(true);
  }, [expandTick]);
  // Baseline metadata — populated once on mount. Drives the "Baseline set on
  // <date>" chip in the header so everyone can see where the app's Day-Zero
  // actually is. Empty until the first baseline reset is applied.
  const [baselineInfo, setBaselineInfo] = useState(null);
  useEffect(() => {
    api
      .get("/stocktake/baseline-info")
      .then((r) => setBaselineInfo(r.data || null))
      .catch(() => {});
  }, []);


  const loadSpend = () => {
    const params = addDateParams({});
    if (dept !== "all") params.department = dept;
    api
      .get("/reports/spend", { params })
      .then((r) => setSpend(r.data))
      .catch(() => toast.error("Could not load spend report"));
  };
  useEffect(() => {
    loadSpend();
  }, [days, dept, dateFrom, dateTo, includeStocktake]);

  const [subsReport, setSubsReport] = useState(null);
  useEffect(() => {
    api
      .get("/reports/substitutes", { params: addDateParams({}) })
      .then((r) => setSubsReport(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, dateFrom, dateTo]);

  useEffect(() => {
    const params = {};
    if (dept !== "all") params.department = dept;
    api
      .get("/reports/cost-per-cover", { params })
      .then((r) => setCostRows(r.data))
      .catch(() => {});
    api
      .get("/reports/price-changes", {
        params: { days: 90, min_delta_pct: 5, ...params },
      })
      .then((r) => setPriceChanges(r.data))
      .catch(() => {});
  }, [dept]);

  useEffect(() => {
    api
      .get("/reports/stock-value-by-department")
      .then((r) => setStockValue(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = addDateParams({});
    if (dept !== "all") params.department = dept;
    api
      .get("/reports/consumption-by-department", { params })
      .then((r) => setConsumption(r.data))
      .catch(() => {});
    api
      .get("/reports/item-consumption", { params })
      .then((r) => setItemConsumption(r.data))
      .catch(() => {});
    // Shrinkage report always includes stocktake movements — that's the
    // whole point. Uses the same date range but drops the include_* toggles.
    const shParams = { ...params };
    delete shParams.include_stocktake;
    api
      .get("/reports/shrinkage", { params: shParams })
      .then((r) => setShrinkage(r.data))
      .catch(() => {});
  }, [days, dept, dateFrom, dateTo, includeStocktake]);

  // Assets reports — refresh whenever the user opens the tab or changes range
  useEffect(() => {
    if (tab !== "assets") return;
    api.get("/reports/assets/value-by-department").then((r) => setAssetValue(r.data)).catch(() => {});
    api.get("/reports/assets/count-by-category").then((r) => setAssetCats(r.data)).catch(() => {});
    api.get("/reports/assets/warranty-timeline", { params: { days: 90 } }).then((r) => setWarranty(r.data)).catch(() => {});
    api.get("/reports/assets/breakage", { params: { days } }).then((r) => setBreakage(r.data)).catch(() => {});
    api.get("/reports/holdings/value").then((r) => setHoldings(r.data)).catch(() => {});
  }, [tab, days]);

  // Machinery reports
  useEffect(() => {
    if (tab !== "machinery") return;
    const params = { days };
    const paramsNoDays = {};
    if (dept !== "all") {
      params.department = dept;
      paramsNoDays.department = dept;
    }
    api.get("/reports/machines/service-spend", { params }).then((r) => setServiceSpend(r.data)).catch(() => {});
    api.get("/reports/machines/upcoming-services", { params: { days: 90, ...paramsNoDays } }).then((r) => setUpcoming(r.data)).catch(() => {});
    api.get("/reports/machines/overdue", { params: paramsNoDays }).then((r) => setOverdue(r.data)).catch(() => {});
    api.get("/reports/machines/downtime", { params }).then((r) => setDowntime(r.data)).catch(() => {});
    api.get("/reports/machines/callout-response", { params }).then((r) => setCalloutResponse(r.data)).catch(() => {});
  }, [tab, days, dept]);

  // Site Services summary — loaded on EVERY tab so the rollup card at the
  // bottom of the page shows across Consumption / Assets / Machinery /
  // Suppliers. Days follows the page selector so spend matches the tab range.
  useEffect(() => {
    api
      .get("/site-services/summary", { params: { days } })
      .then((r) => setSiteServicesSummary(r.data))
      .catch(() => {});
  }, [days]);

  // Pie of stock value by dept — client-side filter to the selected dept
  // so the pie visually collapses to the scoped slice.
  const pieData = useMemo(
    () => {
      const rows = stockValue?.by_department || [];
      const scoped = dept === "all" ? rows : rows.filter((d) => d.department === dept);
      return scoped.map((d) => ({ name: d.department, value: d.value }));
    },
    [stockValue, dept]
  );

  // Same treatment for the Asset & Machinery value pies.
  const assetPieData = useMemo(() => {
    const rows = assetValue?.by_department || [];
    return dept === "all" ? rows : rows.filter((d) => d.department === dept);
  }, [assetValue, dept]);

  const scopedStockValueTotal = useMemo(() => {
    if (dept === "all") return stockValue?.total_value;
    return (stockValue?.by_department || [])
      .filter((d) => d.department === dept)
      .reduce((s, d) => s + (d.value || 0), 0);
  }, [stockValue, dept]);

  const scopedAssetValueTotal = useMemo(() => {
    if (dept === "all") return assetValue?.total_value;
    return (assetValue?.by_department || [])
      .filter((d) => d.department === dept)
      .reduce((s, d) => s + (d.value || 0), 0);
  }, [assetValue, dept]);

  const byDay = spend?.by_day || [];
  const byDept = spend?.by_department || [];
  const bySupplier = spend?.by_supplier || [];

  return (
    <CollapseAllContext.Provider value={{ collapseTick, expandTick }}>
    <div className="p-4 md:p-6 lg:p-8" data-testid="reports-page">
      {/* Print-only cover block — hidden on-screen, appears as the first page
          of the PDF/printout. Compact "at-a-glance" summary so anyone reading
          the printed report immediately sees the scope + who generated it. */}
      <div
        className="hidden print:block mb-6 pb-4 border-b-2 border-black"
        data-testid="reports-print-cover"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="label-eyebrow">Podlab · Inventory Reports</div>
            <h1 className="font-display text-4xl mt-1">
              {tab === "assets" ? "Assets" : tab === "machinery" ? "Machinery" : "Consumables"} — last {days} days
            </h1>
            <div className="text-sm text-[#001740] mt-2">
              <b>Department:</b> {dept === "all" ? "All departments" : dept}
            </div>
            {tab === "consumables" && spend?.total !== undefined && (
              <div className="text-sm text-[#001740] mt-1">
                <b>Total spend ({days}d):</b>{" "}
                <span className="font-mono-num">
                  ${fmt(spend.total)} {spend.currency || "AUD"}
                </span>
              </div>
            )}
          </div>
          <div className="text-right text-xs text-[#001740] shrink-0">
            <div>
              <b>Generated by:</b> {user?.name || "—"}
            </div>
            <div>
              <b>Generated at:</b>{" "}
              {new Date().toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="label-eyebrow">Spend · cost-per-cover · departments</div>
          <h1 className="font-display text-5xl leading-none mt-2">Reports</h1>
          {baselineInfo?.applied_at && (
            <div
              data-testid="reports-baseline-chip"
              className="mt-2 inline-flex items-center gap-1.5 border-2 border-black rounded-full bg-[#FFF7D6] px-2.5 py-1 text-[11px] font-bold"
              title={`Set by ${baselineInfo.user_name || "manager"} · ${baselineInfo.entries_count || 0} items snapped`}
            >
              <ClipboardCheck size={12} strokeWidth={2.5} />
              Baseline set{" "}
              {new Date(baselineInfo.applied_at).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            data-testid="reports-collapse-all"
            onClick={() => setCollapseTick((t) => t + 1)}
            className="nb-btn bg-white px-3 py-2 text-xs font-bold"
            title="Close every collapsible section on this page"
          >
            Collapse all
          </button>
          <button
            type="button"
            data-testid="reports-expand-all"
            onClick={() => setExpandTick((t) => t + 1)}
            className="nb-btn bg-white px-3 py-2 text-xs font-bold"
            title="Open every collapsible section on this page"
          >
            Expand all
          </button>
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              data-testid={`reports-range-${d}`}
              onClick={() => {
                setDays(d);
                setDateFrom("");
                setDateTo("");
              }}
              className={`nb-btn px-4 py-2 ${
                !useCustomRange && days === d ? "nb-btn-primary" : "bg-white"
              }`}
            >
              {d}D
            </button>
          ))}
          {/* Custom-range date pickers — supersede `days` when either is set.
              Great for month-end reporting: "1 Aug – 31 Aug". */}
          <div
            className={`nb-btn ${useCustomRange ? "nb-btn-primary" : "bg-white"} px-2 py-1 flex items-center gap-1 text-xs flex-wrap`}
            data-testid="reports-custom-range"
          >
            <span className="label-eyebrow whitespace-nowrap">From</span>
            <input
              data-testid="reports-date-from"
              type="date"
              className="nb-input h-8 text-xs px-2 w-[7.5rem] sm:w-[8.5rem]"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <span className="label-eyebrow whitespace-nowrap">To</span>
            <input
              data-testid="reports-date-to"
              type="date"
              className="nb-input h-8 text-xs px-2 w-[7.5rem] sm:w-[8.5rem]"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
            {useCustomRange && (
              <button
                data-testid="reports-clear-range"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="text-white/90 hover:text-white ml-1"
                title="Clear range — go back to preset days"
                aria-label="clear date range"
              >
                ×
              </button>
            )}
          </div>
          <label
            data-testid="reports-include-stocktake"
            className={`nb-btn px-3 py-2 flex items-center gap-2 cursor-pointer text-xs ${
              includeStocktake ? "nb-btn-primary" : "bg-white"
            }`}
            title="Include stocktake reconciliation movements (physical-count adjustments)"
          >
            <input
              type="checkbox"
              className="accent-black"
              checked={includeStocktake}
              onChange={(e) => setIncludeStocktake(e.target.checked)}
            />
            Include stocktake
          </label>
          <a
            data-testid="reports-export-csv"
            href={`${API}/reports/export?${
              useCustomRange
                ? `${dateFrom ? `date_from=${dateFrom}` : ""}${dateTo ? `${dateFrom ? "&" : ""}date_to=${dateTo}` : ""}`
                : `days=${days}`
            }${
              dept !== "all" ? `&department=${encodeURIComponent(dept)}` : ""
            }&auth=${encodeURIComponent(getToken() || "")}`}
            className="nb-btn nb-btn-dark px-4 py-2 flex items-center gap-1.5 no-underline text-white"
          >
            <Download size={16} strokeWidth={2.5} /> CSV
          </a>
          <button
            data-testid="reports-print-pdf"
            onClick={() => window.print()}
            title="Print or Save as PDF — uses your browser's print dialog"
            className="nb-btn bg-white px-4 py-2 flex items-center gap-1.5"
          >
            <Printer size={16} strokeWidth={2.5} /> PDF
          </button>
        </div>
      </div>

      <div className="mb-5">
        <DepartmentChips value={dept} onChange={setDept} testidPrefix="reports-dept" />
      </div>

      {/* Tab bar — Consumables / Assets / Machinery */}
      <div
        className="flex flex-wrap gap-2 mb-5"
        data-testid="reports-tabs"
      >
        {[
          { k: "consumables", label: "Consumables", icon: BarChart3 },
          { k: "assets", label: "Assets", icon: Monitor },
          { k: "machinery", label: "Machinery", icon: Cog },
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            data-testid={`reports-tab-${k}`}
            onClick={() => setTab(k)}
            className={`nb-btn px-4 py-2 flex items-center gap-2 ${
              tab === k ? "nb-btn-dark" : "bg-white"
            }`}
          >
            <Icon size={16} strokeWidth={2.5} /> {label}
          </button>
        ))}
      </div>

      {tab === "consumables" && (
       <>
      {/* Top row: total + daily line + (pie when all-depts / scoped value card otherwise).
          Each value/chart card uses `flex flex-col` so the eyebrow sits at the
          top of the card and the subtitle at the bottom via `mt-auto` — this
          keeps the four cards visually balanced regardless of how tall the
          Daily-spend / pie chart makes the row. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div
          className="nb-card p-5 flex flex-col"
          style={{ background: "linear-gradient(135deg, #DFEEF7, #ffffff)" }}
          data-testid="reports-total-spend"
        >
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Total spend ({days}d)</div>
            <DollarSign size={22} strokeWidth={2.5} className="text-[#228EBF]" />
          </div>
          <div className="font-mono-num font-bold text-3xl sm:text-4xl mt-2 leading-none">
            {spend?.currency_symbol || "$"}
            {fmt(spend?.total_spend)}
          </div>
          <div className="label-eyebrow mt-auto pt-3">
            Money paid to suppliers (IN)
            {dept !== "all" && <> · {dept}</>}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setTotalConsumedDrillOpen(true)}
          className="nb-card p-5 flex flex-col text-left transition hover:-translate-y-0.5 hover:shadow-lg group"
          style={{ background: "linear-gradient(135deg, #FFF3E4, #ffffff)" }}
          data-testid="reports-total-consumed"
          title="View items behind this total"
        >
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Total consumed ({days}d)</div>
            <TrendingDown size={22} strokeWidth={2.5} className="text-[#C7A43B]" />
          </div>
          <div className="font-mono-num font-bold text-3xl sm:text-4xl mt-2 leading-none">
            ${fmt(
              (itemConsumption?.rows || []).reduce(
                (s, r) => s + Number(r.spend || 0),
                0,
              ),
            )}
          </div>
          <div className="label-eyebrow mt-auto pt-3 flex items-center justify-between gap-2">
            <span>
              Value of stock taken (OUT)
              {dept !== "all" && <> · {dept}</>}
            </span>
            <span className="text-[#9A6F00] opacity-40 group-hover:opacity-100 whitespace-nowrap">
              Tap for breakdown →
            </span>
          </div>
        </button>

        <ChartCard title="Daily spend" icon={BarChart3} testid="reports-by-day" collapsible defaultOpen>
          {byDay.length ? (
            <div className="h-44" data-testid="chart-daily">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byDay} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v, n) => [`$${fmt(v)}`, n]}
                    labelStyle={{ fontFamily: "Lexend" }}
                    contentStyle={{ borderRadius: 12, fontSize: 12 }}
                  />
                  {(spend?.by_day_departments || []).length > 0 && (
                    <Legend
                      wrapperStyle={{ fontSize: 11 }}
                      iconType="circle"
                      iconSize={7}
                    />
                  )}
                  {(spend?.by_day_departments || []).length > 0 ? (
                    // Stacked bars, one per dept, colours match the dept palette
                    // used everywhere else on the page.
                    (spend.by_day_departments || []).map((d, i, arr) => (
                      <Bar
                        key={d}
                        dataKey={d}
                        stackId="spend"
                        fill={colorFor(d)}
                        radius={
                          i === arr.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]
                        }
                      />
                    ))
                  ) : (
                    <Bar dataKey="spend" fill="#228EBF" radius={[6, 6, 0, 0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[#607087] py-8 text-center">
              No spend recorded in this period.
            </div>
          )}
        </ChartCard>

        {dept === "all" ? (
        <ChartCard
          title="Stock value by dept"
          icon={PieIcon}
          testid="reports-stock-pie"
          right={
            <button
              data-testid="stock-value-drill-btn"
              onClick={() => setStockValueDrillOpen(true)}
              className="nb-btn bg-white px-2.5 py-1 text-xs flex items-center gap-1"
              title="Full item-by-item breakdown"
            >
              View items
            </button>
          }
        >
          {pieData.length > 0 && pieData.some((d) => d.value > 0) ? (
            <div className="h-52" data-testid="chart-stock-pie">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="48%"
                    innerRadius={32}
                    outerRadius={68}
                    paddingAngle={2}
                    onClick={(entry) => setDept(entry.name)}
                    // Inside-the-slice labels so they don't get clipped by the
                    // card. Only render when the slice is big enough.
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                      if (percent < 0.06) return null;
                      const RAD = Math.PI / 180;
                      const r = innerRadius + (outerRadius - innerRadius) * 0.55;
                      const x = cx + r * Math.cos(-midAngle * RAD);
                      const y = cy + r * Math.sin(-midAngle * RAD);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#fff"
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={11}
                          fontWeight={700}
                          style={{ pointerEvents: "none" }}
                        >
                          {`${(percent * 100).toFixed(0)}%`}
                        </text>
                      );
                    }}
                    labelLine={false}
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={colorFor(entry.name)} cursor="pointer" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [`$${fmt(v)}`, n]}
                    contentStyle={{ borderRadius: 12, fontSize: 12 }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11, lineHeight: "1.4" }}
                    formatter={(value) => (
                      <span style={{ color: "#001740" }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[#607087] py-8 text-center">
              Add unit cost + department to materials.
            </div>
          )}
          {scopedStockValueTotal > 0 && (
            <div className="label-eyebrow text-center mt-1">
              Total: ${fmt(scopedStockValueTotal)}
              {dept !== "all" && (
                <span className="text-[#5B3A99]"> · {dept} only</span>
              )}
            </div>
          )}
        </ChartCard>
        ) : (
          <button
            type="button"
            onClick={() => setStockValueDrillOpen(true)}
            className="nb-card p-5 flex flex-col text-left transition hover:-translate-y-0.5 hover:shadow-lg group"
            style={{ background: "linear-gradient(135deg, #F1E7FA, #ffffff)" }}
            data-testid="reports-stock-value-scoped"
          >
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Stock value · {dept}</div>
              <PieIcon size={22} strokeWidth={2.5} className="text-[#5B3A99]" />
            </div>
            <div className="font-mono-num font-bold text-3xl sm:text-4xl mt-2 leading-none">
              ${fmt(scopedStockValueTotal)}
            </div>
            <div className="label-eyebrow mt-auto pt-3 flex items-center justify-between gap-2">
              <span>on-hand value in {dept}</span>
              <span className="text-[#5B3A99] opacity-40 group-hover:opacity-100 whitespace-nowrap">
                Tap for breakdown →
              </span>
            </div>
          </button>
        )}
      </div>

      {/* Middle row: spend by dept bar + consumption line — only when All Depts */}
      {dept === "all" && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <ChartCard title="Spend by department" icon={BarChart3} testid="reports-spend-by-dept" collapsible defaultOpen>
          {byDept.length ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byDept} layout="vertical" margin={{ left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="department" type="category" tick={{ fontSize: 12 }} width={90} />
                  <Tooltip formatter={(v) => `$${fmt(v)}`} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Bar dataKey="spend" radius={[0, 6, 6, 0]}>
                    {byDept.map((d) => (
                      <Cell key={d.department} fill={colorFor(d.department)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[#607087] py-8 text-center">No data yet.</div>
          )}
        </ChartCard>

        <ChartCard
          title="Consumption by dept"
          icon={Activity}
          testid="reports-consumption"
          right={
            (consumption?.departments || []).includes("Unassigned") ? (
              <button
                data-testid="reports-fix-unassigned-btn"
                onClick={() => setFixUnassignedOpen(true)}
                className="nb-btn bg-[#FFF7DB] border-[#C7A43B] text-[#5B4500] px-2.5 py-1 text-xs flex items-center gap-1"
                title="Reassign Unassigned items / backfill dept on legacy movements"
              >
                <Wand2 size={12} strokeWidth={2.5} /> Fix Unassigned
              </button>
            ) : null
          }
        >
          {consumption?.series?.length ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={consumption.series} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `$${fmt(v)}`} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {(consumption.departments || []).map((d) => (
                    <Line
                      key={d}
                      type="monotone"
                      dataKey={d}
                      stroke={colorFor(d)}
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[#607087] py-8 text-center">
              No outbound movements in this period.
            </div>
          )}
        </ChartCard>
      </div>
      )}

      {/* Price changes — flag items where unit cost shifted. Positioned right
          under the daily rhythm charts so managers see cost drift alongside
          spend/consumption before drilling into per-supplier / per-item detail. */}
      <div className="nb-card p-5 mt-5" data-testid="reports-price-changes">
        <button
          type="button"
          data-testid="reports-price-changes-toggle"
          onClick={() => setPriceChangesExpanded((x) => !x)}
          className="w-full flex items-center justify-between gap-3 mb-1 text-left"
          aria-expanded={priceChangesExpanded}
        >
          <div>
            <div className="label-eyebrow">Last 90 days · ≥ 5% change</div>
            <h3 className="font-display text-xl flex items-center gap-2">
              Price changes
              {priceChanges?.rows?.length > 0 && (
                <span
                  className="text-[11px] font-bold text-white bg-[#8A1F1F] px-1.5 py-0.5 rounded"
                  data-testid="reports-price-changes-count"
                >
                  {priceChanges.rows.length}
                </span>
              )}
              <ChevronDown
                size={16}
                strokeWidth={2.5}
                className={`transition-transform ${
                  priceChangesExpanded ? "rotate-180" : ""
                }`}
              />
            </h3>
          </div>
          <div className="label-eyebrow text-[#607087] text-right">
            {priceChangesExpanded
              ? "Tap to collapse"
              : "Tap to expand · flags hikes & drops"}
          </div>
        </button>
        {priceChangesExpanded &&
          (!priceChanges?.rows?.length ? (
            <div className="text-sm text-[#607087] py-8 text-center">
              No price moves above 5% recorded in the last 90 days.
              <div className="text-xs mt-1">
                Enter a unit cost when receiving stock and we&apos;ll flag drift here.
              </div>
            </div>
          ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b-2 border-black text-left">
                  <th className="py-2 pr-3 font-display">Item</th>
                  <th className="py-2 pr-3 font-display">Supplier</th>
                  <th className="py-2 pr-3 font-display text-right">Prev</th>
                  <th className="py-2 pr-3 font-display text-right">New</th>
                  <th className="py-2 pr-3 font-display text-right">Δ%</th>
                  <th className="py-2 pr-3 font-display">When</th>
                  <th className="py-2 pr-3 font-display">By</th>
                  {isManager && <th className="py-2 pr-1 font-display"></th>}
                </tr>
              </thead>
              <tbody>
                {priceChanges.rows.slice(0, 50).map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#E0E5EA]"
                    data-testid={`price-change-row-${r.id}`}
                  >
                    <td className="py-2 pr-3 font-display truncate max-w-[260px]">
                      {r.stock_name}
                    </td>
                    <td className="py-2 pr-3 text-[#607087]">{r.supplier || "—"}</td>
                    <td className="py-2 pr-3 font-mono-num text-right text-[#607087]">
                      ${Number(r.prev_unit_cost || 0).toFixed(2)}
                    </td>
                    <td className="py-2 pr-3 font-mono-num text-right font-bold">
                      ${Number(r.unit_cost || 0).toFixed(2)}
                    </td>
                    <td
                      className="py-2 pr-3 font-mono-num text-right font-bold"
                      style={{ color: r.delta_pct > 0 ? "#E5263F" : "#4DA86A" }}
                    >
                      {r.delta_pct > 0 ? "↑" : "↓"} {Math.abs(r.delta_pct).toFixed(1)}%
                    </td>
                    <td className="py-2 pr-3 text-xs text-[#607087]">
                      {new Date(r.recorded_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-3 text-xs">{r.user_name}</td>
                    {isManager && (
                      <td className="py-2 pr-1">
                        <button
                          data-testid={`price-change-delete-${r.id}`}
                          onClick={async () => {
                            if (
                              !window.confirm(
                                `Remove this price change for "${r.stock_name}"?\n\nThis only deletes the price-history row (legacy/test data). Movements and current cost are unaffected.`,
                              )
                            )
                              return;
                            try {
                              await api.delete(`/reports/price-changes/${r.id}`);
                              setPriceChanges((pc) =>
                                pc
                                  ? {
                                      ...pc,
                                      rows: pc.rows.filter((x) => x.id !== r.id),
                                      total_changes: Math.max(
                                        0,
                                        (pc.total_changes || 1) - 1,
                                      ),
                                    }
                                  : pc,
                              );
                              toast.success("Price change removed");
                            } catch (e) {
                              toast.error(
                                e.response?.data?.detail || "Delete failed",
                              );
                            }
                          }}
                          className="nb-btn bg-white p-1.5 text-[#8A1F1F]"
                          title="Remove this price change entry (legacy/test data)"
                          aria-label="delete price change"
                        >
                          <X size={12} strokeWidth={2.5} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ))}
      </div>

      {/* Shrinkage / discrepancies — surfaces every stocktake-flagged
          movement in the window. Purely audit-facing: don't confuse this
          with regular consumption. */}
      <div className="nb-card p-5 mt-5" data-testid="reports-shrinkage">
        <button
          type="button"
          data-testid="reports-shrinkage-toggle"
          onClick={() => setShrinkageExpanded((x) => !x)}
          className="w-full flex items-center justify-between mb-1 flex-wrap gap-2 text-left"
          aria-expanded={shrinkageExpanded}
        >
          <div>
            <div className="label-eyebrow">Physical count discrepancies</div>
            <h3 className="font-display text-xl flex items-center gap-2">
              Shrinkage &amp; found
              {shrinkage?.rows?.length > 0 && (
                <span className="text-[11px] font-bold text-white bg-black px-1.5 py-0.5 rounded">
                  {shrinkage.rows.length}
                </span>
              )}
              <ChevronDown
                size={16}
                strokeWidth={2.5}
                className={`transition-transform ${
                  shrinkageExpanded ? "rotate-180" : ""
                }`}
              />
            </h3>
          </div>
          <div className="label-eyebrow text-[#607087]">
            From posted stocktake counts
          </div>
        </button>
        {shrinkageExpanded && (
          <div className="mt-3">
            {!shrinkage ? (
          <div className="text-sm text-[#607087] py-6 text-center">Loading…</div>
        ) : shrinkage.rows.length === 0 ? (
          <div className="text-sm text-[#607087] py-6 text-center">
            No stocktake adjustments in this window. Post counts via
            Sidebar → Stocktake to see them here.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="nb-card-sm p-3 bg-[#FCE4E4] border-[#8A1F1F]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#8A1F1F]">
                  Shrinkage value
                </div>
                <div className="font-display text-2xl mt-1" data-testid="shrinkage-total-loss">
                  ${shrinkage.total_shrinkage_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  {shrinkage.total_shrinkage_qty} units missing
                </div>
              </div>
              <div className="nb-card-sm p-3 bg-[#DFF7DF] border-[#2E7D32]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#2E7D32]">
                  Found value
                </div>
                <div className="font-display text-2xl mt-1" data-testid="shrinkage-total-found">
                  ${shrinkage.total_found_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  {shrinkage.total_found_qty} units found
                </div>
              </div>
              <div className="nb-card-sm p-3 bg-[#FFF9EC] border-[#7A4A00]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#7A4A00]">
                  Net loss
                </div>
                <div className="font-display text-2xl mt-1" data-testid="shrinkage-net-loss">
                  ${shrinkage.net_loss_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-[#6B6B6B]">Shrinkage − Found</div>
              </div>
            </div>
            {shrinkage.repeat_offenders && shrinkage.repeat_offenders.length > 0 && (
              <div
                className="nb-card-sm p-3 mb-4 bg-[#FFF3F0] border-[#8A1F1F]"
                data-testid="shrinkage-repeat-offenders"
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} strokeWidth={2.5} className="text-[#8A1F1F]" />
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#8A1F1F]">
                    Repeat offenders — shrinkage on multiple days
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {shrinkage.repeat_offenders.map((ro) => (
                    <div
                      key={ro.stock_id}
                      data-testid={`shrinkage-repeat-${ro.stock_id}`}
                      className="border-2 border-[#8A1F1F] rounded-lg px-2.5 py-1.5 bg-white flex items-center gap-2 text-xs"
                      title={`Last seen ${new Date(ro.last_seen).toLocaleDateString()}`}
                    >
                      <span className="font-bold">{ro.item_name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8A1F1F] text-white font-bold">
                        {ro.occurrences}×
                      </span>
                      <span className="font-mono-num text-[#8A1F1F]">
                        −${ro.total_shrinkage_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="overflow-x-auto scrollbar-clean">
              <table className="w-full text-sm" data-testid="shrinkage-table">
                <thead>
                  <tr className="border-b-2 border-black text-left">
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider">When</th>
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider">Item</th>
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider">Dept</th>
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider">Type</th>
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider text-right">Qty</th>
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider text-right">Value</th>
                    <th className="py-2 pr-3 text-xs uppercase tracking-wider">By</th>
                    {isManager && <th className="py-2 pr-1 text-xs uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {shrinkage.rows.slice(0, 100).map((r) => (
                    <tr
                      key={r.id}
                      className={`border-b border-[#EEE] ${
                        r.is_repeat_offender ? "bg-[#FFF3F0]" : ""
                      }`}
                      data-testid={`shrinkage-row-${r.id}`}
                    >
                      <td className="py-2 pr-3 text-xs font-mono-num whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 pr-3 font-bold">
                        <span className="inline-flex items-center gap-1.5">
                          {r.item_name}
                          {r.is_repeat_offender && (
                            <span
                              data-testid={`shrinkage-repeat-flag-${r.id}`}
                              title="Repeat offender — shrinkage recorded on multiple days"
                              className="text-[9px] px-1.5 py-0.5 border-2 border-[#8A1F1F] bg-[#8A1F1F] text-white rounded-md font-bold uppercase tracking-wide"
                            >
                              Repeat
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs">{r.department}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`text-[10px] px-2 py-0.5 border-2 rounded font-bold ${
                            r.action === "OUT"
                              ? "border-[#8A1F1F] bg-[#FCE4E4] text-[#8A1F1F]"
                              : "border-[#2E7D32] bg-[#DFF7DF] text-[#2E7D32]"
                          }`}
                        >
                          {r.action === "OUT" ? "shrinkage" : "found"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono-num">
                        {r.qty} {r.unit}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono-num font-bold">
                        ${r.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 pr-3 text-xs">{r.user_name}</td>
                      {isManager && (
                        <td className="py-2 pr-1">
                          {r.action === "OUT" && (
                            <button
                              data-testid={`shrinkage-mark-baseline-${r.id}`}
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `Reclassify "${r.item_name}" (−${r.qty} ${r.unit}) as a Baseline reset?\n\nIt will drop off Shrinkage but stay in History with a "Baseline reset (reclassified)" note. Use this when a stocktake was posted with Baseline mode OFF by mistake.`,
                                  )
                                )
                                  return;
                                try {
                                  await api.post(
                                    `/reports/shrinkage/${r.id}/mark-baseline`,
                                  );
                                  toast.success("Reclassified as baseline");
                                  setShrinkage((sh) =>
                                    sh
                                      ? {
                                          ...sh,
                                          rows: sh.rows.filter(
                                            (x) => x.id !== r.id,
                                          ),
                                          total_shrinkage_qty:
                                            r.action === "OUT"
                                              ? Math.max(
                                                  0,
                                                  (sh.total_shrinkage_qty || 0) - r.qty,
                                                )
                                              : sh.total_shrinkage_qty,
                                          total_shrinkage_value:
                                            r.action === "OUT"
                                              ? Math.max(
                                                  0,
                                                  (sh.total_shrinkage_value || 0) - r.value,
                                                )
                                              : sh.total_shrinkage_value,
                                        }
                                      : sh,
                                  );
                                } catch (e) {
                                  toast.error(
                                    e.response?.data?.detail || "Failed",
                                  );
                                }
                              }}
                              className="nb-btn bg-[#FFF7D6] px-2 py-1 text-[10px] font-bold whitespace-nowrap"
                              title="This was a baseline reset — remove from Shrinkage"
                            >
                              → Baseline
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
          </div>
        )}
      </div>


      {/* Bottom section — two rows:
          Row 1 (2 cols): Spend by supplier · Cost per cover — each grows
          to its content height, no wasted whitespace.
          Row 2 (full-width): Top consumers table. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5 items-start">
        <ChartCard
          title="Spend by supplier"
          icon={TrendingUp}
          testid="reports-by-supplier"
          collapsible
          defaultOpen={false}
          headerRight={
            bySupplier.length ? (
              <span className="text-[11px] font-bold text-white bg-black px-1.5 py-0.5 rounded">
                {bySupplier.length}
              </span>
            ) : null
          }
          collapsedPreview={
            bySupplier.length ? (
              <div
                className="flex flex-col gap-2"
                data-testid="reports-by-supplier-preview"
              >
                <div className="label-eyebrow text-[#607087]">
                  Top {Math.min(3, bySupplier.length)} suppliers · tap header to see all
                </div>
                {bySupplier.slice(0, 3).map((s) => (
                  <button
                    key={s.supplier_id}
                    type="button"
                    onClick={() =>
                      setSpendDetail({
                        id: s.supplier_id,
                        name: s.supplier_name,
                      })
                    }
                    data-testid={`reports-supplier-preview-${s.supplier_id}`}
                    className="flex items-center gap-3 nb-card-sm p-2.5 text-left w-full hover:bg-[#EAF3FA] hover:border-[#5B3A99] transition-colors"
                  >
                    <div className="flex-1 min-w-0 truncate font-display">
                      {s.supplier_name}
                    </div>
                    <div className="font-mono-num font-bold">
                      ${fmt(s.spend)}
                    </div>
                  </button>
                ))}
                {bySupplier.length > 3 && (
                  <div className="text-[11px] text-[#607087] italic px-1">
                    + {bySupplier.length - 3} more suppliers hidden
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-[#607087] py-4 text-center">
                No supplier spend yet.
              </div>
            )
          }
        >
          {bySupplier.length ? (
            <div className="flex flex-col gap-2">
              {bySupplier.map((s) => (
                <button
                  key={s.supplier_id}
                  type="button"
                  onClick={() =>
                    setSpendDetail({ id: s.supplier_id, name: s.supplier_name })
                  }
                  data-testid={`reports-supplier-row-${s.supplier_id}`}
                  className="group flex items-center gap-3 nb-card-sm p-2.5 text-left w-full hover:bg-[#EAF3FA] hover:border-[#5B3A99] transition-colors"
                  title="View every movement contributing to this total"
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-display flex items-center gap-1.5 flex-wrap">
                      {s.supplier_name}
                      {s.is_processor && (
                        <span
                          className="text-[9px] font-black tracking-wider uppercase bg-[#FFE9C2] border border-[#7A5300] text-[#7A5300] rounded px-1.5 py-0.5"
                          title={
                            s.processor_spend != null
                              ? `Includes $${fmt(s.processor_spend)} toll-processing across ${s.processor_po_count || s.po_count} companion PO(s)`
                              : `${s.po_count} companion PO(s) — toll-processing spend`
                          }
                        >
                          Processor
                        </span>
                      )}
                    </div>
                    {dept === "all" && s.by_department?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.by_department.slice(0, 4).map((d) => (
                          <span
                            key={d.department}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-black/20 font-mono-num"
                            style={{
                              background: `${deptColor(d.department)}22`,
                              color: "#001740",
                            }}
                            title={`${d.department}: $${fmt(d.spend)}`}
                          >
                            {d.department} ${fmt(d.spend)}
                          </span>
                        ))}
                        {s.by_department.length > 4 && (
                          <span className="text-[10px] text-[#607087] italic self-center">
                            +{s.by_department.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="font-mono-num font-bold">${fmt(s.spend)}</div>
                  <Eye
                    size={14}
                    strokeWidth={2.5}
                    className="text-[#5B3A99] opacity-40 group-hover:opacity-100 shrink-0"
                  />
                </button>
              ))}
              <div className="label-eyebrow text-[#607087] mt-1 px-1">
                Click a supplier to see every movement in the total.
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#607087] py-8 text-center">
              No supplier spend yet.
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Cost per cover"
          icon={Scissors}
          testid="reports-cost-per-cover"
          collapsible
          defaultOpen
          right={
            <button
              data-testid="export-cost-per-cover-csv"
              onClick={() => {
                if (!costRows.length) return;
                const header = "Item,Unit,Unit cost,Template,Qty per unit,Cost per cover";
                const rows = costRows.flatMap((r) =>
                  r.templates.map((t) =>
                    [
                      `"${r.name.replace(/"/g, '""')}"`,
                      r.unit,
                      Number(r.unit_cost || 0).toFixed(4),
                      `"${t.name.replace(/"/g, '""')}"`,
                      Number(t.qty_per_unit || 0),
                      Number(t.cost_per_cover || 0).toFixed(4),
                    ].join(","),
                  ),
                );
                const csv = [header, ...rows].join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `cost-per-cover-${dept === "all" ? "all-depts" : dept}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              disabled={!costRows.length}
              className="nb-btn bg-white px-2.5 py-1 text-xs flex items-center gap-1 disabled:opacity-40"
              title="Download as CSV"
            >
              <Download size={12} strokeWidth={2.5} /> CSV
            </button>
          }
        >
          {costRows.length === 0 ? (
            <div className="text-sm text-[#607087] py-8 text-center">
              Add unit cost + template yields to materials to see cost-per-cover.
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[280px] overflow-y-auto scrollbar-clean pr-1">
              {costRows.map((row) => (
                <div
                  key={row.id}
                  className="nb-card-sm p-3"
                  data-testid={`cpc-${row.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-base truncate">{row.name}</div>
                    <div className="font-mono-num text-sm text-[#607087]">
                      ${fmt(row.unit_cost)}/{row.unit}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-2">
                    {row.templates.map((t) => (
                      <div
                        key={t.name}
                        className="nb-card-sm p-1.5 text-center"
                        style={{ background: "#DFEEF7" }}
                      >
                        <div className="label-eyebrow truncate">{t.name}</div>
                        <div className="font-mono-num font-bold text-sm">
                          ${fmt(t.cost_per_cover)}
                        </div>
                        <div className="text-[10px] text-[#607087]">
                          {t.qty_per_unit}/{row.unit}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Substitutes by supplier"
          icon={ReplaceIcon}
          testid="reports-substitutes"
          defaultOpen={false}
          headerRight={
            subsReport?.total ? (
              <span
                data-testid="reports-substitutes-total"
                className="text-[11px] font-bold text-white bg-[#8A1F1F] px-1.5 py-0.5 rounded"
              >
                {subsReport.total}
              </span>
            ) : null
          }
          collapsedPreview={
            <div className="text-sm text-[#607087]">
              {subsReport?.total
                ? `${subsReport.total} substitute receipt${subsReport.total === 1 ? "" : "s"} from ${subsReport.by_supplier.length} supplier${subsReport.by_supplier.length === 1 ? "" : "s"} · tap to see`
                : "No substitutes received in this period."}
            </div>
          }
        >
          {subsReport?.by_supplier?.length ? (
            <div className="flex flex-col gap-1.5">
              {subsReport.by_supplier.map((r) => (
                <div
                  key={r.supplier_id}
                  data-testid={`reports-substitutes-row-${r.supplier_id}`}
                  className="flex items-center gap-3 nb-card-sm p-2.5"
                >
                  <div className="flex-1 min-w-0 truncate font-display">{r.supplier_name}</div>
                  <div className="font-mono-num font-bold">{r.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#607087] py-4 text-center">
              No substitutes received in this period.
            </div>
          )}
        </ChartCard>

        {/* Row 2 full-width: Top consumers table. */}
        <div className="nb-card p-5 lg:col-span-2" data-testid="reports-item-consumption">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            data-testid="reports-top-consumers-toggle"
            onClick={() => setTopConsumersExpanded((x) => !x)}
            aria-expanded={topConsumersExpanded}
            className="flex-1 text-left flex items-center gap-2"
          >
            <div>
              <div className="label-eyebrow">By item · last {days} days</div>
              <h3 className="font-display text-xl flex items-center gap-2">
                Top consumers
                {itemConsumption?.rows?.length > 0 && (
                  <span className="text-[11px] font-bold text-white bg-black px-1.5 py-0.5 rounded">
                    {itemConsumption.rows.length}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  strokeWidth={2.5}
                  className={`transition-transform ${
                    topConsumersExpanded ? "rotate-180" : ""
                  }`}
                />
              </h3>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="export-top-consumers-csv"
              onClick={() => {
                const rows = itemConsumption?.rows || [];
                if (!rows.length) return;
                const head = ["Item","Type","Department","Taken","Unit","Sub-unit total","Sub-unit","Spend (AUD)"];
                const body = rows.map((r) => [
                  r.name,
                  r.type,
                  r.department || "",
                  r.qty_pack,
                  r.unit,
                  r.pack_size && r.pack_unit ? r.qty_sub : "",
                  r.pack_size && r.pack_unit ? r.pack_unit : "",
                  r.spend.toFixed(2),
                ]);
                const csv = [head, ...body]
                  .map((row) =>
                    row
                      .map((c) => {
                        const s = String(c ?? "");
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                      })
                      .join(",")
                  )
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `top-consumers-${days}d.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              disabled={!itemConsumption?.rows?.length}
              className="nb-btn bg-white px-3 py-1.5 text-sm flex items-center gap-1"
            >
              <Download size={14} strokeWidth={2.5} /> CSV
            </button>
            <div className="label-eyebrow text-[#607087] hidden sm:block">
              Pack items also show sub-unit totals (kg / L / pcs)
            </div>
          </div>
        </div>
        {topConsumersExpanded && (!itemConsumption?.rows?.length ? (
          <div className="text-sm text-[#607087] py-8 text-center">
            No consumption recorded in the last {days} days.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-black text-left">
                  <th className="py-2 pr-3 font-display">Item</th>
                  <th className="py-2 pr-3 font-display">Type</th>
                  <th className="py-2 pr-3 font-display text-right">Taken</th>
                  <th className="py-2 pr-3 font-display text-right">Sub-unit total</th>
                  <th className="py-2 pr-3 font-display text-right">Spend</th>
                </tr>
              </thead>
              <tbody>
                {itemConsumption.rows.slice(0, 20).map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#E0E5EA]"
                    data-testid={`item-consumption-row-${r.id}`}
                  >
                    <td className="py-2 pr-3 font-display truncate max-w-[260px]">
                      {r.name}
                    </td>
                    <td className="py-2 pr-3 text-[#607087]">{r.type}</td>
                    <td className="py-2 pr-3 font-mono-num text-right">
                      {r.qty_pack} {r.unit}
                    </td>
                    <td className="py-2 pr-3 font-mono-num text-right text-[#9A6F00]">
                      {r.pack_size && r.pack_unit
                        ? `${r.qty_sub.toLocaleString()} ${r.pack_unit}`
                        : "—"}
                    </td>
                    <td className="py-2 pr-3 font-mono-num text-right font-bold">
                      ${fmt(r.spend)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      </div>

      {/* Turnover ratio — sits directly under Top consumers so managers
          eyeball slow vs fast movers back-to-back. */}
      <div className="mt-5">
        <TurnoverCard />
      </div>

       </>
      )}

      {tab === "assets" && (
        <div className="flex flex-col gap-5" data-testid="reports-assets-section">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div
              className="nb-card p-5"
              style={{ background: "linear-gradient(135deg, #F1E7FA, #ffffff)" }}
              data-testid="reports-assets-total"
            >
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">Assets + Machinery value</div>
                <Monitor size={22} strokeWidth={2.5} className="text-[#5B3A99]" />
              </div>
              <div className="font-mono-num font-bold text-3xl sm:text-4xl mt-2 leading-none">
                ${fmt(
                  dept === "all"
                    ? holdings?.total_value
                    : (holdings?.by_department || [])
                        .filter((d) => d.department === dept)
                        .reduce((s, d) => s + (d.value || 0), 0),
                )}
              </div>
              <div className="label-eyebrow mt-2">
                {dept === "all"
                  ? `${holdings?.asset_count || 0} asset${holdings?.asset_count === 1 ? "" : "s"} · ${holdings?.machine_count || 0} machine${holdings?.machine_count === 1 ? "" : "s"}`
                  : `${dept} only`}
              </div>
            </div>

            <ChartCard
              title="Value by department"
              icon={BarChart3}
              testid="reports-holdings-by-dept"
            >
              {(() => {
                const rows = holdings?.by_department || [];
                const scoped = dept === "all" ? rows : rows.filter((d) => d.department === dept);
                return scoped.length ? (
                  <div className="h-44" style={{ minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <BarChart data={scoped} layout="vertical" margin={{ left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="department" type="category" tick={{ fontSize: 12 }} width={90} />
                        <Tooltip
                          formatter={(v, n) => [`$${fmt(v)}`, n === "asset_value" ? "Assets" : "Machinery"]}
                          contentStyle={{ borderRadius: 12, fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "asset_value" ? "Assets" : "Machinery")} />
                        <Bar dataKey="asset_value" stackId="v" fill="#5B3A99" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="machine_value" stackId="v" fill="#C7A43B" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-sm text-[#607087] py-8 text-center">
                    No assets or machines registered yet.
                  </div>
                );
              })()}
            </ChartCard>

            <ChartCard title="Value by category" icon={PieIcon} testid="reports-holdings-by-cat" collapsible defaultOpen>
              {(() => {
                // Bugfix: pie must respect the dept filter. by_category from the
                // backend is a global roll-up; derive a scoped view from the
                // matrix when the user has picked a specific department so a
                // Grinder that lives in Production never appears under 3DP.
                let scopedCats = holdings?.by_category || [];
                if (dept !== "all" && holdings?.matrix) {
                  const bucket = new Map();
                  for (const r of holdings.matrix) {
                    if (r.department !== dept) continue;
                    const cur = bucket.get(r.category) || { category: r.category, value: 0, count: 0 };
                    cur.value += r.value || 0;
                    cur.count += r.count || 0;
                    bucket.set(r.category, cur);
                  }
                  scopedCats = Array.from(bucket.values()).sort(
                    (a, b) => b.value - a.value,
                  );
                }
                if (!scopedCats.length) {
                  return (
                    <div className="text-sm text-[#607087] py-8 text-center">
                      Nothing to show.
                    </div>
                  );
                }
                // v197 — Reception has 30+ categories on Production; a raw
                // pie legend swallowed the pie itself. Roll everything
                // past the top 8 into a single "Other" slice for the
                // pie, then render a compact scrollable legend below
                // showing EVERY category (so nothing is hidden from
                // audit) with its exact value + share.
                const TOP_N = 8;
                const PALETTE = [
                  "#5B3A99", "#228EBF", "#7A4DBA", "#1E8E8E",
                  "#C2185B", "#E5A72E", "#3A7A4C", "#B14A2A",
                  "#9CA3AF",
                ];
                const total = scopedCats.reduce((s, c) => s + (c.value || 0), 0) || 1;
                const top = scopedCats.slice(0, TOP_N);
                const rest = scopedCats.slice(TOP_N);
                const restTotal = rest.reduce((s, c) => s + (c.value || 0), 0);
                const pieData = [
                  ...top.map((c, i) => ({ name: c.category, value: c.value, color: PALETTE[i] })),
                ];
                if (rest.length) {
                  pieData.push({
                    name: `Other (${rest.length})`,
                    value: restTotal,
                    color: PALETTE[TOP_N],
                  });
                }
                // Full colour map so the legend below matches slice colours
                // for the top set and shares one grey for the tail.
                const colorFor = (name, i) => {
                  if (i < TOP_N) return PALETTE[i];
                  return PALETTE[TOP_N];
                };
                return (
                  <>
                    <div className="h-52" style={{ minWidth: 0 }}>
                      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={32}
                            outerRadius={68}
                            paddingAngle={2}
                            label={({ percent }) =>
                              percent > 0.06 ? `${(percent * 100).toFixed(0)}%` : ""
                            }
                            labelLine={false}
                            fontSize={11}
                          >
                            {pieData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(v, n) => [`$${fmt(v)}`, n]}
                            contentStyle={{ borderRadius: 12, fontSize: 12 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div
                      className="mt-2 pt-3 border-t border-[#E4E7EB]"
                      data-testid="value-by-cat-legend"
                    >
                      <div className="flex items-baseline justify-between mb-1.5">
                        <div className="label-eyebrow">
                          All {scopedCats.length} categories
                        </div>
                        <div className="text-[10px] text-[#607087]">
                          Total ${fmt(total)}
                        </div>
                      </div>
                      <div className="max-h-44 overflow-y-auto scrollbar-clean pr-1">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {scopedCats.map((c, i) => {
                            const pct = ((c.value || 0) / total) * 100;
                            return (
                              <li
                                key={c.category}
                                className="flex items-center gap-2 min-w-0"
                                title={`${c.category} · $${fmt(c.value)} · ${pct.toFixed(1)}%`}
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full shrink-0 border border-black/10"
                                  style={{ background: colorFor(c.category, i) }}
                                />
                                <span className="truncate flex-1">{c.category}</span>
                                <span className="font-mono-num text-[#607087] shrink-0">
                                  {pct.toFixed(pct < 1 ? 1 : 0)}%
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </>
                );
              })()}
            </ChartCard>
          </div>

          {/* Value by department × category — expandable matrix */}
          <ChartCard
            title="Value by department × category"
            icon={TrendingUp}
            testid="reports-holdings-matrix"
            right={
              <button
                data-testid="holdings-matrix-csv"
                disabled={!holdings?.matrix?.length}
                onClick={() => {
                  const header = "Department,Category,Count,Avg cost,Total value,Items";
                  const rows = (holdings?.matrix || []).map((r) =>
                    [
                      `"${(r.department || "").replace(/"/g, '""')}"`,
                      `"${(r.category || "").replace(/"/g, '""')}"`,
                      r.count,
                      Number(r.unit_cost_avg || 0).toFixed(2),
                      Number(r.value || 0).toFixed(2),
                      `"${(r.items || [])
                        .map((it) => `${it.name} ($${Number(it.cost || 0).toFixed(2)})`)
                        .join(" · ")
                        .replace(/"/g, '""')}"`,
                    ].join(","),
                  );
                  const csv = [header, ...rows].join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `holdings-matrix.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="nb-btn bg-white px-2.5 py-1 text-xs flex items-center gap-1 disabled:opacity-40"
              >
                <Download size={12} strokeWidth={2.5} /> CSV
              </button>
            }
          >
            {(() => {
              const rowsAll = holdings?.matrix || [];
              const rows = dept === "all" ? rowsAll : rowsAll.filter((r) => r.department === dept);
              if (rows.length === 0) {
                return (
                  <div className="text-sm text-[#607087] py-8 text-center">
                    No assets or machines yet.
                  </div>
                );
              }
              // Group rows by department for the outer accordion
              const byDept = {};
              rows.forEach((r) => {
                (byDept[r.department] = byDept[r.department] || []).push(r);
              });
              return (
                <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto scrollbar-clean pr-1">
                  {Object.entries(byDept).map(([d, list]) => {
                    const deptTotal = list.reduce((s, r) => s + (r.value || 0), 0);
                    const isOpen = holdingsExpanded[d];
                    return (
                      <div
                        key={d}
                        data-testid={`holdings-dept-${d}`}
                        className="border-2 border-black rounded-xl bg-white"
                      >
                        <button
                          onClick={() =>
                            setHoldingsExpanded((s) => ({ ...s, [d]: !s[d] }))
                          }
                          className="w-full flex items-center gap-3 p-3 text-left"
                          data-testid={`holdings-dept-toggle-${d}`}
                        >
                          <ChevronDown
                            size={16}
                            strokeWidth={2.5}
                            className={`transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`}
                          />
                          <div className="font-display text-base flex-1">{d}</div>
                          <div className="label-eyebrow text-[#607087]">
                            {list.length} categor{list.length === 1 ? "y" : "ies"}
                          </div>
                          <div className="font-mono-num font-bold shrink-0">
                            ${fmt(deptTotal)}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="border-t-2 border-black/10">
                            {list.map((r) => (
                              <div
                                key={`${d}-${r.category}`}
                                data-testid={`holdings-row-${d}-${r.category}`}
                                className="p-3 border-b border-black/5 last:border-b-0"
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-display text-sm flex-1 min-w-0 truncate">
                                    {r.category}
                                  </span>
                                  <span className="label-eyebrow text-[#607087]">
                                    {r.count} × avg ${fmt(r.unit_cost_avg)}
                                  </span>
                                  <span className="font-mono-num font-bold w-24 text-right">
                                    ${fmt(r.value)}
                                  </span>
                                </div>
                                {r.items?.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {r.items.map((it) => (
                                      <span
                                        key={it.id}
                                        className={`text-xs px-2 py-0.5 rounded-md border ${
                                          it.kind === "machine"
                                            ? "bg-[#FFF7DB] text-[#5B4500] border-[#7A4A00]/40"
                                            : "bg-[#F1E7FA] text-[#5B3A99] border-[#5B3A99]/40"
                                        }`}
                                      >
                                        {it.name} · ${fmt(it.cost)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </ChartCard>

          <ChartCard title="Warranty timeline" icon={ShieldCheck} testid="reports-warranty" collapsible defaultOpen>
            {!warranty ||
            (warranty.expired_count === 0 && warranty.expiring_count === 0) ? (
              <div className="text-sm text-[#607087] py-8 text-center">
                No warranties expiring in the next 90 days.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: "Already expired", items: warranty.expired, cls: "bg-[#FCE4E4] border-[#8A1F1F]" },
                  ...warranty.buckets.map((b) => ({
                    label: b.label,
                    items: b.items,
                    cls:
                      b.key === "0-30"
                        ? "bg-[#FFE9C2] border-[#7A4A00]"
                        : b.key === "30-60"
                        ? "bg-[#FFF7DB] border-[#5B4500]"
                        : "bg-[#E6F4EA] border-[#1F6F3E]",
                  })),
                ].map((g) => (
                  <div
                    key={g.label}
                    data-testid={`warranty-bucket-${g.label}`}
                    className={`border-2 rounded-xl p-3 ${g.cls}`}
                  >
                    <div className="label-eyebrow">{g.label}</div>
                    <div className="font-display text-2xl">{g.items.length}</div>
                    {g.items.length > 0 && (
                      <ul className="mt-2 flex flex-col gap-1 text-xs">
                        {g.items.slice(0, 5).map((it) => (
                          <li key={it.id} className="truncate">
                            <span className="font-semibold">{it.name}</span>
                            <span className="text-[#607087]">
                              {" "}
                              · {it.warranty_until}
                            </span>
                          </li>
                        ))}
                        {g.items.length > 5 && (
                          <li className="italic text-[#607087]">
                            +{g.items.length - 5} more
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ChartCard>

          <ChartCard
            title={`Loss / breakage log (last ${days}d)`}
            icon={PackageX}
            testid="reports-breakage"
          >
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <div className="border-2 border-black rounded-xl p-3 bg-[#FCE4E4]">
                <div className="label-eyebrow">Broken</div>
                <div className="font-display text-2xl">{breakage?.broken_count ?? 0}</div>
              </div>
              <div className="border-2 border-black rounded-xl p-3 bg-[#EEE]">
                <div className="label-eyebrow">Retired</div>
                <div className="font-display text-2xl">{breakage?.retired_count ?? 0}</div>
              </div>
              <div className="border-2 border-black rounded-xl p-3 bg-[#F1E7FA]">
                <div className="label-eyebrow">Cost written off</div>
                <div className="font-mono-num font-bold text-2xl">
                  ${fmt(breakage?.cost_written_off)}
                </div>
              </div>
            </div>
            {!breakage?.items?.length ? (
              <div className="text-sm text-[#607087] py-4 text-center">
                No assets marked broken or retired in this period.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-black text-left">
                      <th className="py-2 pr-3 font-display">Asset</th>
                      <th className="py-2 pr-3 font-display">Category</th>
                      <th className="py-2 pr-3 font-display">Department</th>
                      <th className="py-2 pr-3 font-display">Status</th>
                      <th className="py-2 pr-3 font-display text-right">Cost</th>
                      <th className="py-2 pr-3 font-display">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakage.items.slice(0, 30).map((it) => (
                      <tr
                        key={it.id}
                        className="border-b border-[#E0E5EA]"
                        data-testid={`breakage-row-${it.id}`}
                      >
                        <td className="py-2 pr-3 font-display truncate max-w-[240px]">{it.name}</td>
                        <td className="py-2 pr-3 text-[#607087]">{it.category || "—"}</td>
                        <td className="py-2 pr-3 text-[#607087]">{it.department || "—"}</td>
                        <td className="py-2 pr-3 text-xs font-semibold">{it.status}</td>
                        <td className="py-2 pr-3 font-mono-num text-right">
                          ${fmt(it.cost)}
                        </td>
                        <td className="py-2 pr-3 text-xs text-[#607087]">
                          {it.updated_at ? new Date(it.updated_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {tab === "machinery" && (
        <div className="flex flex-col gap-5" data-testid="reports-machinery-section">
          {/* Combined-export toolbar (all machines in current dept + range). */}
          <div className="flex items-center justify-end gap-2 -mb-2 print:hidden">
            {isManager && (
              <button
                data-testid="machinery-retag-consumables"
                onClick={async () => {
                  if (!window.confirm(
                    "Re-tag legacy 'Spare parts' entries whose linked stock is a Consumable?\n\n" +
                    "This scans every auto-generated Machine Service entry and flips " +
                    "kind → 'Consumables' when the note references a Consumable stock " +
                    "item (Detailing Agent, Fusing Agent, cleaning rolls, etc).\n\n" +
                    "Safe to re-run — nothing else is touched.",
                  )) return;
                  try {
                    const { data } = await api.post(
                      "/machines/services/retag-consumables",
                    );
                    toast.success(
                      `Re-tagged ${data.updated} entr${
                        data.updated === 1 ? "y" : "ies"
                      } as Consumables${
                        data.updated ? " · reloading…" : ""
                      }`,
                    );
                    if (data.updated) {
                      // Refetch so the pie/bar/tiles reflect the retagged rows.
                      const params = { days };
                      if (dept !== "all") params.department = dept;
                      api
                        .get("/reports/machines/service-spend", { params })
                        .then((r) => setServiceSpend(r.data))
                        .catch(() => {});
                    }
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.detail || "Could not re-tag",
                    );
                  }
                }}
                className="nb-btn bg-[#E6F4EA] text-[#1F6F3E] border-[#1F6F3E] px-3 py-2 text-xs flex items-center gap-1"
                title="Bulk-flip legacy 'Spare parts' entries to 'Consumables' where the linked stock is a Consumable"
              >
                🧴 Re-tag Consumables
              </button>
            )}
            <button
              data-testid="machinery-export-csv"
              disabled={!serviceSpend?.by_machine?.length}
              onClick={() => {
                const rows = serviceSpend.by_machine.map((m) =>
                  [
                    `"${(m.name || "").replace(/"/g, '""')}"`,
                    `"${(m.department || "").replace(/"/g, '""')}"`,
                    `"${(m.category || "").replace(/"/g, '""')}"`,
                    Number(m.by_kind?.Service || 0).toFixed(2),
                    Number(m.by_kind?.["Spare parts"] || 0).toFixed(2),
                    Number(m.by_kind?.Consumables || 0).toFixed(2),
                    Number(m.by_kind?.Repair || 0).toFixed(2),
                    Number(m.by_kind?.["Call-out"] || 0).toFixed(2),
                    Number(m.total || 0).toFixed(2),
                    m.count,
                  ].join(","),
                );
                const csv = [
                  "Machine,Department,Category,Service ($),Spare parts ($),Consumables ($),Repair ($),Call-out ($),Total ($),Entries",
                  ...rows,
                ].join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `machinery-spend-${dept === "all" ? "all-depts" : dept}-${days}d.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-40"
              title="Download all machines as CSV"
            >
              <Download size={12} strokeWidth={2.5} /> CSV (all machines)
            </button>
            <button
              data-testid="machinery-export-pdf"
              disabled={!serviceSpend?.by_machine?.length}
              onClick={() => {
                const rows = serviceSpend.by_machine.map((m) => [
                  m.name,
                  m.department || "—",
                  m.category || "—",
                  Number(m.by_kind?.Service || 0),
                  Number(m.by_kind?.["Spare parts"] || 0),
                  Number(m.by_kind?.Consumables || 0),
                  Number(m.by_kind?.Repair || 0),
                  Number(m.by_kind?.["Call-out"] || 0),
                  Number(m.total || 0),
                  m.count,
                ]);
                rows.push([
                  "TOTAL",
                  "",
                  "",
                  Number(serviceSpend.by_kind?.Service || 0),
                  Number(serviceSpend.by_kind?.["Spare parts"] || 0),
                  Number(serviceSpend.by_kind?.Consumables || 0),
                  Number(serviceSpend.by_kind?.Repair || 0),
                  Number(serviceSpend.by_kind?.["Call-out"] || 0),
                  Number(serviceSpend.total || 0),
                  serviceSpend.service_count,
                ]);
                printReport(
                  "Machinery spend by machine",
                  `Last ${days} days · ${dept === "all" ? "All departments" : dept} · ${serviceSpend.service_count} entries · Total $${fmt(serviceSpend.total)}`,
                  ["Machine", "Dept", "Category", "Service", "Spare parts", "Consumables", "Repair", "Call-out", "Total", "Entries"],
                  rows,
                  `Generated ${new Date().toLocaleString()} · Podlab Inventory`,
                );
              }}
              className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-40"
              title="Print / save all machines as PDF"
            >
              <Printer size={12} strokeWidth={2.5} /> PDF (all machines)
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div
              className="nb-card p-5"
              style={{ background: "linear-gradient(135deg, #FFE9C2, #ffffff)" }}
              data-testid="reports-service-spend-total"
            >
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">Total machine spend ({days}d)</div>
                <Wrench size={22} strokeWidth={2.5} className="text-[#7A4A00]" />
              </div>
              <div className="font-mono-num font-bold text-3xl sm:text-4xl mt-2 leading-none">
                ${fmt(serviceSpend?.total)}
              </div>
              <div className="label-eyebrow mt-2">
                {serviceSpend?.service_count || 0} log entr
                {serviceSpend?.service_count === 1 ? "y" : "ies"} · Service · Spare parts · Consumables · Repair · Call-out
              </div>
            </div>

            <ChartCard
              title="Total machine spend over time"
              icon={BarChart3}
              testid="reports-service-spend-trend"
            >
              {serviceSpend?.by_day?.length ? (
                <div className="h-44 lg:col-span-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={serviceSpend.by_day}
                      margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(v) => [`$${fmt(v)}`, "Total cost"]}
                        contentStyle={{ borderRadius: 12, fontSize: 12 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#C7A43B"
                        strokeWidth={2.5}
                        dot={{ r: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-[#607087] py-8 text-center">
                  No log entries in this period.
                </div>
              )}
            </ChartCard>

            <ChartCard
              title="Top spend per machine"
              icon={TrendingUp}
              testid="reports-spend-per-machine"
            >
              {serviceSpend?.by_machine?.length ? (
                <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto scrollbar-clean pr-1">
                  {serviceSpend.by_machine.slice(0, 10).map((m) => {
                    const t = m.total || 0.0001;
                    const sPct = ((m.by_kind?.Service || 0) / t) * 100;
                    const pPct = ((m.by_kind?.["Spare parts"] || 0) / t) * 100;
                    const cPct = ((m.by_kind?.Consumables || 0) / t) * 100;
                    const rPct = ((m.by_kind?.Repair || 0) / t) * 100;
                    const oPct = ((m.by_kind?.["Call-out"] || 0) / t) * 100;
                    return (
                      <button
                        key={m.machine_id}
                        data-testid={`spend-machine-${m.machine_id}`}
                        onClick={() => setMachineDrill(m)}
                        className="text-left nb-card-sm p-2.5 hover:bg-[#F1E7FA] transition-colors cursor-pointer"
                        title="Click to see full breakdown"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0 truncate font-display">
                            {m.name}
                          </div>
                          <div className="label-eyebrow text-[#607087] shrink-0">
                            {m.count}×
                          </div>
                          <div className="font-mono-num font-bold shrink-0">
                            ${fmt(m.total)}
                          </div>
                        </div>
                        {/* Stacked mini-bar: Service / Spare parts / Consumables / Repair / Call-out */}
                        <div className="mt-1.5 flex h-1.5 rounded-full overflow-hidden bg-[#EEE]">
                          {sPct > 0 && (
                            <div
                              style={{ width: `${sPct}%`, background: "#C7A43B" }}
                              title={`Service $${fmt(m.by_kind?.Service)}`}
                            />
                          )}
                          {pPct > 0 && (
                            <div
                              style={{ width: `${pPct}%`, background: "#0A3A7A" }}
                              title={`Spare parts $${fmt(m.by_kind?.["Spare parts"])}`}
                            />
                          )}
                          {cPct > 0 && (
                            <div
                              style={{ width: `${cPct}%`, background: "#1F6F3E" }}
                              title={`Consumables $${fmt(m.by_kind?.Consumables)}`}
                            />
                          )}
                          {rPct > 0 && (
                            <div
                              style={{ width: `${rPct}%`, background: "#8A1F1F" }}
                              title={`Repair $${fmt(m.by_kind?.Repair)}`}
                            />
                          )}
                          {oPct > 0 && (
                            <div
                              style={{ width: `${oPct}%`, background: "#7A2E00" }}
                              title={`Call-out $${fmt(m.by_kind?.["Call-out"])}`}
                            />
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {/* Legend */}
                  <div className="flex items-center gap-3 text-[10px] font-semibold text-[#607087] pt-1 flex-wrap">
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#C7A43B" }} />
                      Service
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#0A3A7A" }} />
                      Spare parts
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#1F6F3E" }} />
                      Consumables
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#8A1F1F" }} />
                      Repair
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#7A2E00" }} />
                      Call-out
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[#607087] py-8 text-center">
                  Log a service entry to populate this list.
                </div>
              )}
            </ChartCard>
          </div>

          {/* Upcoming + Overdue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard
              title="Overdue services"
              icon={AlertCircle}
              testid="reports-overdue"
            >
              {!overdue?.items?.length ? (
                <div className="text-sm text-[#607087] py-8 text-center">
                  Nothing overdue — nice.
                </div>
              ) : (
                <ul className="flex flex-col gap-2 max-h-[280px] overflow-y-auto scrollbar-clean pr-1">
                  {overdue.items.map((m) => (
                    <li
                      key={m.id}
                      data-testid={`overdue-row-${m.id}`}
                      className="nb-card-sm p-3 flex items-center gap-3 border-2 border-[#8A1F1F] bg-[#FCE4E4]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-display truncate">{m.name}</div>
                        <div className="label-eyebrow text-[#607087]">
                          {m.category || "—"} · was due {m.next_service_at}
                        </div>
                      </div>
                      <div className="font-mono-num font-bold text-[#8A1F1F] shrink-0">
                        {m.days_overdue}d
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ChartCard>

            <ChartCard
              title="Upcoming services (90d)"
              icon={Clock}
              testid="reports-upcoming"
            >
              {!upcoming?.items?.length ? (
                <div className="text-sm text-[#607087] py-8 text-center">
                  Nothing scheduled in the next 90 days.
                </div>
              ) : (
                <ul className="flex flex-col gap-2 max-h-[280px] overflow-y-auto scrollbar-clean pr-1">
                  {upcoming.items.map((m) => (
                    <li
                      key={m.id}
                      data-testid={`upcoming-row-${m.id}`}
                      className={`nb-card-sm p-3 flex items-center gap-3 ${
                        m.days_until <= 14
                          ? "bg-[#FFE9C2] border-2 border-[#7A4A00]"
                          : ""
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-display truncate">{m.name}</div>
                        <div className="label-eyebrow text-[#607087]">
                          {m.category || "—"} · {m.next_service_at}
                        </div>
                      </div>
                      <div className="font-mono-num font-bold shrink-0">
                        {m.days_until}d
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ChartCard>
          </div>

          <ChartCard
            title={`Downtime (last ${days}d)`}
            icon={Activity}
            testid="reports-downtime"
            collapsible
            defaultOpen
          >
            {!downtime?.items?.length ? (
              <div className="text-sm text-[#607087] py-8 text-center">
                No machine downtime recorded — all running smoothly.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="border-b-2 border-black text-left">
                    <th className="py-2 pr-3 font-display">Machine</th>
                    <th className="py-2 pr-3 font-display">Category</th>
                    <th className="py-2 pr-3 font-display text-right">Downtime (days)</th>
                    <th className="py-2 pr-3 font-display text-right">Incidents</th>
                    <th className="py-2 pr-3 font-display">Current</th>
                  </tr>
                </thead>
                <tbody>
                  {downtime.items.map((r) => (
                    <tr
                      key={r.machine_id}
                      className="border-b border-[#E0E5EA]"
                      data-testid={`downtime-row-${r.machine_id}`}
                    >
                      <td className="py-2 pr-3 font-display">{r.name}</td>
                      <td className="py-2 pr-3 text-[#607087]">{r.category || "—"}</td>
                      <td className="py-2 pr-3 font-mono-num text-right font-bold">
                        {r.downtime_days}
                      </td>
                      <td className="py-2 pr-3 font-mono-num text-right">{r.incidents.length}</td>
                      <td className="py-2 pr-3 text-xs">
                        {r.currently_out_of_service ? (
                          <span className="px-2 py-0.5 rounded-full bg-[#FCE4E4] text-[#8A1F1F] font-semibold">
                            still out
                          </span>
                        ) : (
                          <span className="text-[#607087]">resolved</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </ChartCard>

          <ChartCard
            title={`Call-out response time (last ${days}d)`}
            icon={PhoneCall}
            testid="reports-callout-response"
            collapsible
            defaultOpen
          >
            {!calloutResponse?.incident_count ? (
              <div className="text-sm text-[#607087] py-8 text-center">
                No call-outs logged in this window — a broken machine gets a
                Call-out entry once you tap the &quot;Call NOW&quot; button on its card.
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <div className="nb-card-sm p-3">
                    <div className="label-eyebrow">Incidents</div>
                    <div className="font-display text-2xl mt-1" data-testid="callout-incidents">
                      {calloutResponse.incident_count}
                    </div>
                  </div>
                  <div className="nb-card-sm p-3 bg-[#FFF9EC] border-[#7A4A00]">
                    <div className="label-eyebrow">Avg response</div>
                    <div className="font-display text-2xl mt-1" data-testid="callout-avg-hours">
                      {calloutResponse.avg_response_hours ?? "—"} h
                    </div>
                    <div className="text-xs text-[#6B6B6B]">Broken → first call-out</div>
                  </div>
                  {calloutResponse.by_supplier?.[0] && (
                    <div className="nb-card-sm p-3 bg-[#FCE4E4] border-[#8A1F1F]">
                      <div className="label-eyebrow text-[#8A1F1F]">Slowest supplier</div>
                      <div className="font-display text-base mt-1 leading-tight" data-testid="callout-slowest">
                        {calloutResponse.by_supplier[0].supplier_name}
                      </div>
                      <div className="font-mono-num font-bold text-[#8A1F1F]">
                        {calloutResponse.by_supplier[0].avg_hours} h avg
                      </div>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full text-sm min-w-[560px]" data-testid="callout-supplier-table">
                    <thead>
                      <tr className="border-b-2 border-black text-left">
                        <th className="py-2 pr-3 font-display">Supplier</th>
                        <th className="py-2 pr-3 font-display text-right">Incidents</th>
                        <th className="py-2 pr-3 font-display text-right">Avg response (h)</th>
                        <th className="py-2 pr-3 font-display text-right">Worst (h)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calloutResponse.by_supplier.map((row) => (
                        <tr
                          key={row.supplier_name}
                          data-testid={`callout-supplier-${row.supplier_name}`}
                          className="border-b border-[#E0E5EA]"
                        >
                          <td className="py-2 pr-3 font-display">{row.supplier_name}</td>
                          <td className="py-2 pr-3 font-mono-num text-right">{row.incidents}</td>
                          <td className="py-2 pr-3 font-mono-num text-right font-bold">
                            {row.avg_hours}
                          </td>
                          <td className="py-2 pr-3 font-mono-num text-right text-[#8A1F1F]">
                            {row.worst_hours}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {/* Service calls, agreements, renewal reminders, turnover ratio,
          data exports — all visible across tabs (not scoped to date range). */}
      <ReportsExtrasSection />

      {/* Site services rollup — visible across all tabs so managers see
          overdue and upcoming compliance-visits alongside the stock reports.
          Spend rolls into the same days-window as the rest of the page. */}
      {siteServicesSummary && (
        <div
          className="mt-5 nb-card p-5"
          data-testid="reports-site-services"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-eyebrow">Site Services</div>
              <h3 className="font-display text-2xl">
                Facility spend & upcoming visits
              </h3>
              <div className="text-xs text-[#607087]">
                Last {siteServicesSummary.days} days ·{" "}
                {siteServicesSummary.spend.visit_count} visit
                {siteServicesSummary.spend.visit_count === 1 ? "" : "s"} logged
              </div>
            </div>
            <button
              onClick={() => { window.location.href = "/site-services"; }}
              className="nb-btn bg-white px-3 py-2 text-xs"
              data-testid="reports-site-services-open"
            >
              Open Site Services →
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className="nb-card-sm p-3"
              style={{ background: "linear-gradient(135deg, #E6F4EA, #ffffff)" }}
              data-testid="reports-ss-total"
            >
              <div className="label-eyebrow">Total spend</div>
              <div className="font-mono-num font-bold text-3xl mt-1">
                ${Number(siteServicesSummary.spend.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div
              className="nb-card-sm p-3"
              style={{ background: siteServicesSummary.overdue_count > 0 ? "#FCE4E4" : "#F4F8FB" }}
              data-testid="reports-ss-overdue"
            >
              <div className="label-eyebrow">Overdue</div>
              <div className={`font-mono-num font-bold text-3xl mt-1 ${siteServicesSummary.overdue_count > 0 ? "text-[#8A1F1F]" : ""}`}>
                {siteServicesSummary.overdue_count}
              </div>
              {siteServicesSummary.overdue_count > 0 && (
                <div className="text-[10px] text-[#8A1F1F] font-bold">action needed</div>
              )}
            </div>
            <div
              className="nb-card-sm p-3"
              style={{ background: "#FFE9C2" }}
              data-testid="reports-ss-upcoming"
            >
              <div className="label-eyebrow">Due in {siteServicesSummary.days} days</div>
              <div className="font-mono-num font-bold text-3xl mt-1">
                {siteServicesSummary.upcoming_count}
              </div>
            </div>
          </div>

          {/* By kind + by provider tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="nb-card-sm p-3">
              <div className="label-eyebrow mb-2">Spend by kind</div>
              {siteServicesSummary.spend.by_kind.length === 0 ? (
                <div className="text-xs text-[#607087] italic">No visits in this window.</div>
              ) : (
                <div className="flex flex-col gap-1">
                  {siteServicesSummary.spend.by_kind.map((r) => (
                    <div
                      key={r.kind}
                      className="flex items-center justify-between text-sm"
                      data-testid={`reports-ss-kind-${r.kind.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div>{r.kind}</div>
                      <div className="font-mono-num font-bold">${r.total.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="nb-card-sm p-3">
              <div className="label-eyebrow mb-2">Upcoming visits</div>
              {siteServicesSummary.upcoming.length === 0 && siteServicesSummary.overdue.length === 0 ? (
                <div className="text-xs text-[#607087] italic">Nothing due — nice.</div>
              ) : (
                <div className="flex flex-col gap-1 max-h-[220px] overflow-y-auto pr-1">
                  {siteServicesSummary.overdue.map((r) => (
                    <div
                      key={"o" + r.provider_id}
                      className="flex items-center justify-between text-sm text-[#8A1F1F]"
                      data-testid={`reports-ss-upcoming-${r.provider_id}`}
                    >
                      <div>
                        <b>{r.name}</b>{" "}
                        <span className="text-[#607087]">· {r.kind}</span>
                      </div>
                      <div className="font-bold text-xs">Overdue {Math.abs(r.days_until)}d</div>
                    </div>
                  ))}
                  {siteServicesSummary.upcoming.map((r) => (
                    <div
                      key={"u" + r.provider_id}
                      className="flex items-center justify-between text-sm"
                      data-testid={`reports-ss-upcoming-${r.provider_id}`}
                    >
                      <div>
                        <b>{r.name}</b>{" "}
                        <span className="text-[#607087]">· {r.kind}</span>
                      </div>
                      <div className="text-xs text-[#607087]">In {r.days_until}d</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Digest settings — visible across all tabs */}
      <div className="mt-5">
        <DigestSettingsCard />
      </div>

      {/* Outbound sender email override — manager-only card */}
      <div className="mt-5">
        <SenderEmailCard />
      </div>

      {/* Recent email deliveries + bounce watcher — manager-only */}
      <div className="mt-5">
        <EmailEventsCard />
      </div>

      {/* Supplier spend drill-down — opened from the Spend by supplier list.
          Refreshing after an inline cost fix pulls fresh data by re-triggering
          the /reports/spend load. */}
      <SupplierSpendDetailDialog
        open={!!spendDetail}
        supplierId={spendDetail?.id}
        supplierName={spendDetail?.name}
        initialDays={days}
        onClose={() => setSpendDetail(null)}
        onFixed={() => loadSpend()}
      />

      <MachineSpendDrillDialog
        open={!!machineDrill}
        machine={machineDrill}
        days={days}
        onClose={() => setMachineDrill(null)}
      />

      <FixUnassignedDialog
        open={fixUnassignedOpen}
        onClose={() => setFixUnassignedOpen(false)}
        onChanged={() => {
          // Re-fetch everything that could be affected: consumption chart,
          // per-item consumption, spend totals, and stock-value pie.
          loadSpend();
          const params = { days };
          if (dept !== "all") params.department = dept;
          api
            .get("/reports/consumption-by-department", { params })
            .then((r) => setConsumption(r.data))
            .catch(() => {});
          api
            .get("/reports/item-consumption", { params })
            .then((r) => setItemConsumption(r.data))
            .catch(() => {});
          api
            .get("/reports/stock-value-by-department")
            .then((r) => setStockValue(r.data))
            .catch(() => {});
        }}
      />

      <AssetCategoryDrillDialog
        open={!!assetCatDrill}
        category={assetCatDrill?.category}
        count={assetCatDrill?.count}
        onClose={() => setAssetCatDrill(null)}
      />

      <StockValueDetailDialog
        open={stockValueDrillOpen}
        department={dept}
        onClose={() => setStockValueDrillOpen(false)}
      />

      <TotalConsumedDetailDialog
        open={totalConsumedDrillOpen}
        rows={itemConsumption?.rows || []}
        department={dept}
        rangeLabel={
          useCustomRange
            ? `${dateFrom || "…"} → ${dateTo || "…"}`
            : `last ${days} days`
        }
        onClose={() => setTotalConsumedDrillOpen(false)}
      />
    </div>
    </CollapseAllContext.Provider>
  );
};

export default Reports;
